import React, { useState, useEffect } from 'react';
import { VAPI_CONFIG } from '../../config';
import { supabase } from '../../lib/supabase';

const KnowledgeBasePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [, setCurrentFileContent] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [assistants, setAssistants] = useState([]);

  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        // Get the current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw new Error(`Authentication error: ${userError.message}`);
        if (!user) throw new Error('Not authenticated. Please log in.');

        // Get the user's organization memberships
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id);

        if (membershipsError) throw new Error(`Failed to fetch organization memberships: ${membershipsError.message}`);
        if (!memberships || memberships.length === 0) throw new Error('No organization found. Please contact your administrator.');

        // Get all assistants for the organization
        const { data: assistantsData, error: assistantsError } = await supabase
          .from('organization_assistants')
          .select(`
            assistant_id,
            organizations (
              name
            )
          `)
          .eq('organization_id', memberships[0].organization_id);

        if (assistantsError) throw new Error(`Failed to fetch assistants: ${assistantsError.message}`);
        if (!assistantsData || assistantsData.length === 0) throw new Error('No assistants found for your organization.');

        setAssistants(assistantsData);
        setSelectedAssistant(assistantsData[0].assistant_id);
      } catch (err) {
        console.error('Error fetching assistants:', err);
        setError(err.message);
      }
    };

    fetchAssistants();
  }, []);

  useEffect(() => {
    if (!selectedAssistant) return;

    if (!VAPI_CONFIG?.SECRET_KEY) {
      setError('Vapi SECRET_KEY is not configured.');
      setLoading(false);
      return;
    }

    // Fetch the current knowledge base content
    const fetchCurrentContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const toolResponse = await fetch(`https://api.vapi.ai/tool/${process.env.REACT_APP_VAPI_SWEATER_KB_ID}`, {
          headers: {
            'Authorization': `Bearer ${VAPI_CONFIG.SECRET_KEY}`
          }
        });

        if (!toolResponse.ok) {
          throw new Error('Failed to fetch current knowledge base');
        }

        const toolData = await toolResponse.json();
        if (toolData.knowledgeBases?.[0]?.fileIds?.[0]) {
          const fileId = toolData.knowledgeBases[0].fileIds[0];
          const fileResponse = await fetch(`https://api.vapi.ai/file/${fileId}`, {
            headers: {
              'Authorization': `Bearer ${VAPI_CONFIG.SECRET_KEY}`
            }
          });

          if (!fileResponse.ok) {
            throw new Error('Failed to fetch file content');
          }

          const fileData = await fileResponse.json();
          if (fileData.url) {
            const contentResponse = await fetch(fileData.url);
            if (!contentResponse.ok) {
              throw new Error('Failed to fetch file content from URL');
            }
            const content = await contentResponse.text();
            setCurrentFileContent(content);
            
            // Parse the markdown content into entries
            const sections = content.split('\n\n').filter(section => section.trim());
            const parsedEntries = sections.map(section => {
              const [question, ...answerParts] = section.split('\n');
              return {
                question: question.replace('# ', '').trim(),
                answer: answerParts.join('\n').trim()
              };
            });
            
            if (parsedEntries.length > 0) {
              setEntries(parsedEntries);
            } else {
              setEntries([{ question: '', answer: '' }]);
            }
          }
        } else {
          setEntries([{ question: '', answer: '' }]);
        }
      } catch (err) {
        console.error('Error fetching current content:', err);
        setError(err.message);
        setEntries([{ question: '', answer: '' }]);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentContent();
  }, [selectedAssistant]);

  const addNewEntry = () => {
    setEntries([...entries, { question: '', answer: '' }]);
  };

  const removeEntry = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    setEntries(newEntries);
  };

  const generateMarkdown = () => {
    return entries
      .filter(entry => entry.question.trim() && entry.answer.trim())
      .map(entry => `# ${entry.question.trim()}\n${entry.answer.trim()}\n`)
      .join('\n');
  };

  const uploadKnowledgeBase = async (e) => {
    e.preventDefault();
    
    // Validate entries
    const validEntries = entries.filter(entry => entry.question.trim() && entry.answer.trim());
    if (validEntries.length === 0) {
      setError('Please add at least one question and answer pair');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setUploadStatus('Generating markdown file...');

      if (!VAPI_CONFIG?.SECRET_KEY) {
        throw new Error('Vapi SECRET_KEY is not configured');
      }

      // Create markdown content
      const markdownContent = generateMarkdown();
      const markdownBlob = new Blob([markdownContent], { type: 'text/markdown' });
      const markdownFile = new File([markdownBlob], 'knowledge-base.md', { type: 'text/markdown' });

      // Upload the file
      const formData = new FormData();
      formData.append('file', markdownFile, 'knowledge-base.md');

      const uploadResponse = await fetch('https://api.vapi.ai/file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${VAPI_CONFIG.SECRET_KEY}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({ message: 'Failed to upload file' }));
        throw new Error(errorData.message || 'Failed to upload file');
      }

      const { id: fileId } = await uploadResponse.json();
      setUploadStatus('File uploaded successfully. Updating query tool...');

      // Update the existing query tool with the new file
      const toolResponse = await fetch(`https://api.vapi.ai/tool/${process.env.REACT_APP_VAPI_SWEATER_KB_ID}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${VAPI_CONFIG.SECRET_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          knowledgeBases: [
            {
              provider: 'google',
              name: 'knowledge-base',
              description: 'Use this knowledge base when the user asks or queries about the product or services',
              fileIds: [fileId]
            }
          ]
        })
      });

      if (!toolResponse.ok) {
        const errorData = await toolResponse.json().catch(() => ({ message: 'Failed to update query tool' }));
        throw new Error(errorData.message || 'Failed to update query tool');
      }

      setUploadStatus('Knowledge base updated successfully!');

      // Fetch the updated content
      const updatedToolResponse = await fetch(`https://api.vapi.ai/tool/${process.env.REACT_APP_VAPI_SWEATER_KB_ID}`, {
        headers: {
          'Authorization': `Bearer ${VAPI_CONFIG.SECRET_KEY}`
        }
      });

      if (!updatedToolResponse.ok) {
        throw new Error('Failed to fetch updated knowledge base');
      }

      const updatedToolData = await updatedToolResponse.json();
      if (updatedToolData.knowledgeBases?.[0]?.fileIds?.[0]) {
        const updatedFileId = updatedToolData.knowledgeBases[0].fileIds[0];
        const updatedFileResponse = await fetch(`https://api.vapi.ai/file/${updatedFileId}`, {
          headers: {
            'Authorization': `Bearer ${VAPI_CONFIG.SECRET_KEY}`
          }
        });

        if (!updatedFileResponse.ok) {
          throw new Error('Failed to fetch updated file content');
        }

        const updatedFileData = await updatedFileResponse.json();
        if (updatedFileData.url) {
          const contentResponse = await fetch(updatedFileData.url);
          if (!contentResponse.ok) {
            throw new Error('Failed to fetch updated file content from URL');
          }
          const content = await contentResponse.text();
          setCurrentFileContent(content);
          
          // Parse the markdown content into entries
          const sections = content.split('\n\n').filter(section => section.trim());
          const parsedEntries = sections.map(section => {
            const [question, ...answerParts] = section.split('\n');
            return {
              question: question.replace('# ', '').trim(),
              answer: answerParts.join('\n').trim()
            };
          });
          
          if (parsedEntries.length > 0) {
            setEntries(parsedEntries);
          } else {
            setEntries([{ question: '', answer: '' }]);
          }
        }
      }
    } catch (err) {
      console.error('Error updating knowledge base:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#6C63FF]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Knowledge Base</h1>
        <div className="bg-red-500/10 border border-red-500 text-red-500 rounded-lg p-4">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Knowledge Base</h1>
      
      {assistants.length > 1 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Assistant
          </label>
          <select
            value={selectedAssistant}
            onChange={(e) => setSelectedAssistant(e.target.value)}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6C63FF] focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            {assistants.map((assistant) => (
              <option key={assistant.assistant_id} value={assistant.assistant_id}>
                {assistant.organizations?.name || 'Unknown Organization'} - {assistant.assistant_id}
              </option>
            ))}
          </select>
        </div>
      )}

      {uploadStatus && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500 text-green-500 rounded-lg">
          {uploadStatus}
        </div>
      )}

      <div className="bg-white dark:bg-[#1E1E2E] rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4 text-[#6C63FF]">Help the agent understand your business, services or products better</h2>
        <form onSubmit={uploadKnowledgeBase} className="space-y-6">
          {entries.map((entry, index) => (
            <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Question or Topic
                  </label>
                  {entries.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEntry(index)}
                      className="text-red-500 hover:text-red-700 text-xs whitespace-nowrap"
                    >
                      (Remove)
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={entry.question}
                  onChange={(e) => updateEntry(index, 'question', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-[#6C63FF] focus:border-[#6C63FF] dark:bg-[#2A2A3C] text-gray-900 dark:text-white"
                  placeholder="Company brief, services, products, cities, anything you feel important"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Answer
                </label>
                <textarea
                  value={entry.answer}
                  onChange={(e) => updateEntry(index, 'answer', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-[#6C63FF] focus:border-[#6C63FF] dark:bg-[#2A2A3C] text-gray-900 dark:text-white"
                  rows="4"
                  placeholder="Enter your answer"
                  required
                />
              </div>
            </div>
          ))}

          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={addNewEntry}
              className="px-4 py-2 text-[#6C63FF] border border-[#6C63FF] rounded-md hover:bg-[#6C63FF] hover:text-white transition-colors"
            >
              Add Another Entry
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#6C63FF] text-white rounded-md hover:bg-[#4A45B2] transition-colors"
              disabled={loading}
            >
              {loading ? 'Uploading...' : 'Update Knowledge Base'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KnowledgeBasePage; 